import AttachmentRounded from '@mui/icons-material/AttachmentRounded';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { FileAttachableType, FileAttachmentCategory } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Control,
  FieldErrors,
  FieldPath,
  FieldPathValue,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { FileAttachment } from '~hooks/useFileAttachment/models';
import { useFileAttachment } from '~hooks/useFileAttachment/useFileAttachment';
import { useInlineUploadError } from '~hooks/useInlineUploadError/useInlineUploadError';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

const maxFileSize = 10;

export const fileAttachmentSchema = yup.array().of(
  yup.object().shape({
    id: yup.string().required(),
    category: yup.string().required('Category is required'),
    description: yup.string().notRequired(),
  }),
);

export interface FileAttachmentsHandler {
  onCancel: () => Promise<boolean>;
  onSubmit: (projectId: string) => Promise<boolean>;
}

interface FileAttachmentsProps<TFieldValues extends FieldValues = FieldValues> {
  allowPerItemUpdates?: boolean;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  fileAttachableId?: string;
  fileAttachableType: FileAttachableType;
  form: UseFormReturn<TFieldValues>;
  name: string;
}

interface FileAttachmentUpdate {
  id: string;
  category: FileAttachmentCategory;
  description: string;
}

interface DirtyFileAttachmentUpdate {
  category?: boolean;
  description?: boolean;
}

/**
 * This component is used to handle file attachments for a `Project`, `Order`, `Job`, or `Load`.
 * It allows the user to upload files, update the category and description of the file, and delete the file.
 *
 * @requires `fileAttachableId` if and only if `allowPerItemUpdates` is true, otherwise does not require `fileAttachableId`
 */
const FileAttachmentsInner = <TFieldValues extends FieldValues = FieldValues>(
  {
    fileAttachableId,
    fileAttachableType,
    control,
    errors,
    name,
    form,
    allowPerItemUpdates = false,
  }: FileAttachmentsProps<TFieldValues>,
  ref: Ref<FileAttachmentsHandler>,
) => {
  const theme = useTheme();
  const {
    getAllFileAttachmentsById,
    deleteFileAttachmentById,
    createFileAttachment,
    updateFileAttachmentById,
  } = useFileAttachment();
  // Once timesheets live on their own model, stop offering "Timesheet" as a generic attachment
  // category so the only way to create one is the new Timesheet flow.
  const hideTimesheetCategory = useFeatureFlag({
    featureFlagKey: FeatureFlags.useNewTimesheet,
  });
  const fieldPath = name as FieldPath<TFieldValues>;
  const watchedAttachments =
    (form.watch(fieldPath) as FileAttachmentUpdate[] | undefined) ?? [];
  const fieldName = name as string;
  const dirtyFieldsByName = form.formState.dirtyFields as Partial<
    Record<string, DirtyFileAttachmentUpdate[]>
  >;
  const dirtyFieldsValue = dirtyFieldsByName[fieldName];
  const attachmentDirtyFields = Array.isArray(dirtyFieldsValue)
    ? dirtyFieldsValue
    : undefined;
  const fieldErrorsByName = errors as Partial<
    Record<string, Array<{ category?: unknown; description?: unknown }>>
  >;
  const attachmentFieldErrors = fieldErrorsByName[fieldName];

  const setAttachmentFormValue = useCallback(
    (
      // Options are required because the lint rule that demands an explicit
      // shouldDirty cannot see through this wrapper to its callers.
      value: FileAttachmentUpdate[],
      options: Parameters<UseFormReturn<TFieldValues>['setValue']>[2],
    ) => {
      form.setValue(
        fieldPath,
        value as FieldPathValue<TFieldValues, FieldPath<TFieldValues>>,
        options,
      );
    },
    [fieldPath, form],
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<Partial<FileAttachment>[]>([]);
  const [isEditingIds, setIsEditingIds] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const { uploadError, clearUploadError, showUploadError } = useInlineUploadError();

  /**
   * On form submission, we'll update all the files in the queue.
   */
  const [updateQueue, setUpdateQueue] = useState<FileAttachmentUpdate[]>([]);

  /**
   * On form submission, we'll delete all the files in the queue.
   */
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);

  /**
   * If a user cancels/closes, we need to delete the attachments
   * that were created during the form session.
   */
  const [createdList, setCreatedList] = useState<string[]>([]);

  /**
   * If a user tries to create file attachments on a new project
   * we need to keep track of the attachments that were created.
   * We'll create them on form submission once we have project_id.
   */
  const [createQueue, setCreateQueue] = useState<File[]>([]);

  useImperativeHandle(ref, () => ({
    /**
     * On form close/cancel, we need to delete all the attachments that were created
     * since attachments are created on file upload, not on form submission.
     */
    onCancel: async () => {
      await Promise.all(createdList.map((id) => deleteFileAttachmentById(id)));
      return true;
    },

    /**
     * On form submission, we need to process all items that were in the queues
     */
    async onSubmit(attachableId: string) {
      return await handleFormSubmission(attachableId);
    },
  }));

  useEffect(() => {
    // This component is reused across dialogs that swap the record in place
    // (no remount), so an inline error from the previous record's failed
    // upload must not render against the newly-loaded one.
    clearUploadError();

    if (!fileAttachableId) return;

    setIsFetching(true);

    const fetchAttachments = async () => {
      let data: FileAttachment[];
      try {
        data = await getAllFileAttachmentsById(fileAttachableId, fileAttachableType);
      } catch {
        // Error already surfaced by useFileAttachment.getAllFileAttachmentsById
        // (toast/Sentry for non-suppressed statuses). Swallow so this
        // fire-and-forget effect does not raise an unhandled rejection when the
        // record is deleted out from under an open drawer. See HORIZON-FE-2MM.
        setIsFetching(false);

        return;
      }

      setAttachmentFormValue(
        data.map((attachment) => {
          return {
            id: attachment.id,
            category: attachment.category,
            description: attachment.description ?? '',
          };
        }),
        { shouldDirty: false },
      );
      setAttachments(data);
      setIsFetching(false);
    };

    fetchAttachments();
    // setAttachmentFormValue is intentionally omitted: it useCallback's on
    // `form`, and callers using <FormProvider/> + useFormContext (e.g.
    // ProjectFormV2) get a fresh `form` reference on every keystroke because
    // FormProvider memoizes its value on `formState`. Including the callback
    // here turned this load-once effect into a per-keystroke refetch of
    // /file_attachments. The body only needs RHF's internally-stable setValue,
    // which closure capture is fine for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileAttachableId, fileAttachableType]);

  /**
   * This is a hacky way to get the ID and file_url in the update queue by using the dirtyFields
   */
  useEffect(() => {
    const indices = (attachmentDirtyFields ?? [])
      .map((field, index) => (field?.description || field?.category ? index : null))
      .filter((index): index is number => index !== null);

    const formValues =
      (form.getValues(fieldPath) as FileAttachmentUpdate[] | undefined) ?? [];

    const updatedAttachments = attachments
      .map((attachment, index): FileAttachmentUpdate | null => {
        if (!indices.includes(index) || !attachment.id) return null;

        const updatedAttachment = formValues[index];
        if (!updatedAttachment) return null;

        return {
          id: attachment.id,
          category: updatedAttachment.category,
          description: updatedAttachment.description,
        };
      })
      .filter((attachment): attachment is FileAttachmentUpdate => attachment !== null);

    setUpdateQueue(updatedAttachments);
  }, [attachmentDirtyFields, attachments, fieldPath, form]);

  const handleFormSubmission = async (attachableId: string) => {
    // 1. I need to create all the attachments that were created
    const createdQueuePromises = createQueue.map((file) => {
      // Deliberately no surfaceValidationErrorInline here: this flush runs
      // mid-submit of the parent form, so a rejected file must go through the
      // hook's toast pipeline rather than an inline message the closing
      // dialog would hide.
      return createFileAttachment(file, attachableId, fileAttachableType);
    });

    const newCreatedAttachments = await Promise.all(createdQueuePromises);

    // 2. I need to find the files created within the updatedQueue list and reassign their ID
    // Since, if they were just created they have an invalid ID in the updatedQueue array
    const newCreatedAttachmentUpdates = updateQueue.map((attachment) => {
      const { id, category, description } = attachment;
      const updatedAttachment = newCreatedAttachments.find((createdAttachment) => {
        const fileName = createdAttachment?.fileName;
        // If the file name matches the ID, then it was just created.
        return fileName === id;
      });

      if (updatedAttachment) {
        const fileName = updatedAttachment.fileName;
        return {
          id: updatedAttachment.id,
          fileName,
          category,
          description,
        };
      }

      return null;
    });

    // 3. I need to update all the attachments that were created and then updated
    newCreatedAttachmentUpdates.forEach((attachment) => {
      if (attachment) {
        updateFileAttachmentById(attachment.id, {
          category: attachment.category,
          description: attachment.description,
        });
      }
    });

    // Removing the attachments that were created and updated already from the updateQueue
    const newUpdatedQueue = updateQueue.filter(
      (attachment) =>
        !newCreatedAttachmentUpdates.some(
          (createdAttachment) =>
            createdAttachment && createdAttachment.fileName === attachment.id,
        ),
    );

    newUpdatedQueue.forEach((attachment) => {
      updateFileAttachmentById(attachment.id, {
        category: attachment.category,
        description: attachment.description,
      });
    });

    deleteQueue.forEach((id) => {
      deleteFileAttachmentById(id);
    });

    return true;
  };

  const handleCreateFileAttachment = (file: File) => {
    if (!file) return;
    if (file.size / 1024 / 1024 > maxFileSize) {
      alert(t('file_upload.max_size', { maxSize: maxFileSize + ' MB' }));
      return;
    }

    clearUploadError();

    if (!fileAttachableId) {
      const newAttachmentForList: Partial<FileAttachment> = {
        fileName: file.name,
        id: file.name,
        category: FileAttachmentCategory.OTHER,
        description: '',
      };
      const newAttachmentForForm: FileAttachmentUpdate = {
        id: file.name,
        category: FileAttachmentCategory.OTHER,
        description: '',
      };

      setCreateQueue((prevCreateQueue) => [...prevCreateQueue, file]);
      setAttachments((prevAttachments) => [...prevAttachments, newAttachmentForList]);

      setAttachmentFormValue([...watchedAttachments, newAttachmentForForm], {
        shouldDirty: false,
      });
      handleToggleEdit(newAttachmentForForm.id);
      return;
    }

    createFileAttachment(file, fileAttachableId, fileAttachableType, {
      surfaceValidationErrorInline: true,
    })
      .then((data) => {
        const newCreatedList = [...createdList, data.id];
        const updatedAttachments = [...attachments, data];

        setCreatedList(newCreatedList);
        setAttachments(updatedAttachments);

        setAttachmentFormValue(
          [
            ...watchedAttachments,
            {
              id: data.id,
              category: data.category,
              description: data.description ?? '',
            },
          ],
          { shouldDirty: false },
        );

        handleToggleEdit(data.id);
      })
      .catch((error) => {
        showUploadError(error, t('error_messages.file_attachment.failed_to_create'));
      });
  };

  const handleDeleteFileAttachment = (id: string) => {
    if (allowPerItemUpdates) {
      setAttachmentFormValue(
        watchedAttachments.filter((att) => att.id !== id),
        {
          shouldDirty: true,
        },
      );
      setAttachments((prevAttachments) =>
        prevAttachments.filter((attachment) => attachment.id !== id),
      );
      deleteFileAttachmentById(id);
      return;
    }
    const deletedAttachment = attachments.find((attachment) => attachment.id === id);
    if (!deletedAttachment) return;

    if (fileAttachableId) {
      setDeleteQueue((prevQueue) => [...prevQueue, id]);
    } else {
      setCreateQueue((prevQueue) =>
        prevQueue.filter((file) => file.name !== deletedAttachment.fileName),
      );
    }
    setAttachments((prevAttachments) =>
      prevAttachments.filter((attachment) => attachment.id !== id),
    );

    setAttachmentFormValue(
      watchedAttachments.filter((att) => att.id !== id),
      {
        shouldDirty: true,
      },
    );
  };

  const handleToggleEdit = (id: string) => {
    if (isEditingIds.includes(id)) {
      setIsEditingIds(isEditingIds.filter((i) => i !== id));
    } else {
      setIsEditingIds([...isEditingIds, id]);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Typography variant="form-section-title" sx={{ mb: 0, mr: 'auto' }}>
          {t('form_fields.other_items')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mr: 1,
          }}
        >
          {t('form_fields.attach_extra_files')}
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => fileInputRef && fileInputRef.current?.click()}
        >
          <AttachmentRounded
            sx={{ width: '16px', height: '16px', mr: 1, rotate: '-90deg' }}
          />
          {t('form_fields.add_items')}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            hidden
            onChange={(e) =>
              e.target.files && handleCreateFileAttachment(e.target.files[0])
            }
          />
        </Button>
        <Typography
          color={'grey'}
          variant="body2"
          sx={{
            mr: 1,
          }}
        >
          {t('file_upload.max_size', { maxSize: maxFileSize + ' MB' })}
        </Typography>
        {uploadError && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {uploadError}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          mt: 1,
        }}
      >
        {(!allowPerItemUpdates || (allowPerItemUpdates && fileAttachableId)) &&
          attachments &&
          attachments.map((attachment, index) => (
            <Grid
              container
              key={attachment?.id}
              sx={{
                borderTop: 1,
                py: 2,
                borderColor: theme.semantic.border,
                ':last-child': { pb: 0 },
              }}
            >
              <Grid
                size={{ xs: 12 }}
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Grid size={{ xs: 11 }}>
                  <FormFieldLabel label="Attachment" />
                  {attachment?.fileUrl ? (
                    <PseudoLink
                      title={attachment?.fileName || '-'}
                      action={() => window.open(attachment?.fileUrl)}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        mr: 'auto',
                      }}
                    >
                      {attachment?.fileName ?? '-'}
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 1 }}>
                  {attachment?.id && isEditingIds.includes(attachment.id) ? (
                    <IconButton
                      size="small"
                      onClick={() => attachment?.id && handleToggleEdit(attachment.id)}
                      disabled={
                        !watchedAttachments[index]?.category ||
                        (allowPerItemUpdates &&
                          (attachmentDirtyFields?.[index]?.category ||
                            attachmentDirtyFields?.[index]?.description))
                      }
                    >
                      <Close
                        sx={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => attachment?.id && handleToggleEdit(attachment.id)}
                    >
                      <Edit
                        sx={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Grid container spacing={2}>
                  <Grid
                    size={{ xs: 4 }}
                    sx={{
                      mr: 0,
                    }}
                  >
                    {attachment?.id && isEditingIds.includes(attachment.id) ? (
                      <AutocompleteFormField
                        list={Object.values(FileAttachmentCategory).filter(
                          (category) =>
                            !hideTimesheetCategory ||
                            category !== FileAttachmentCategory.TIMESHEET,
                        )}
                        control={control}
                        errors={errors}
                        name={`${name}[${index}].category`}
                        getLabel={(item) => item}
                        getValue={(item) => item}
                        label="Category"
                        isRequired
                      />
                    ) : (
                      <>
                        <FormFieldLabel
                          label={t('common.category')}
                          labelColor={
                            attachmentFieldErrors?.[index]?.category ? 'error' : 'default'
                          }
                        />
                        <Chip
                          label={watchedAttachments[index]?.category || '-'}
                          color={
                            attachmentFieldErrors?.[index]?.category ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </>
                    )}
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    {attachment?.id && isEditingIds.includes(attachment.id) ? (
                      <TextFormField
                        multiline
                        control={control}
                        errors={errors}
                        name={`${name}[${index}].description`}
                        label="Description"
                      />
                    ) : (
                      <>
                        <FormFieldLabel
                          label={t('common.description')}
                          labelColor={
                            attachmentFieldErrors?.[index]?.description
                              ? theme.palette.error.main
                              : theme.semantic.textStrong
                          }
                        />
                        <Typography variant="body1">
                          {watchedAttachments[index]?.description}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
                {attachment?.id && isEditingIds.includes(attachment.id) && (
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      mt: 1,
                    }}
                  >
                    <Button
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (attachment?.id) handleDeleteFileAttachment(attachment.id);
                      }}
                      sx={{ mr: 'auto' }}
                    >
                      {t('actions.remove')}
                    </Button>
                    <Button
                      onClick={() => {
                        attachment?.id && handleToggleEdit(attachment?.id);
                        allowPerItemUpdates &&
                          attachment?.id &&
                          updateFileAttachmentById(attachment.id, {
                            category: watchedAttachments[index]?.category,
                            description: watchedAttachments[index]?.description,
                          });
                      }}
                      disabled={!watchedAttachments[index]?.category}
                    >
                      {t('actions.confirm')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          ))}
        <LoadingSpinner isVisible={isFetching} sx={{ py: 2 }} />
      </Box>
    </>
  );
};

const FileAttachments = forwardRef(FileAttachmentsInner) as <
  TFieldValues extends FieldValues = FieldValues,
>(
  props: FileAttachmentsProps<TFieldValues> & { ref?: Ref<FileAttachmentsHandler> },
) => ReturnType<typeof FileAttachmentsInner>;

export default FileAttachments;
